<template>
    <C1FormGroup>
        <C1Typeahead
            :id="id"
            :label="!hideLabel ? getLabel : ''"
            :label-for="id"
            :options="locationsSearched"
            :placeholder="getPlaceholder"
            label-key="label"
            :search-min-length="2"
            :disabled="$_seekerFieldByCode('desired_locations').read_only"
            clean-after-select
            @search="searchLocations"
            @hit="attachLocation"
        >
            <template v-slot:icon>
                <FontAwesomeIcon :icon="['far', 'map-marker-alt']" />
            </template>

            <template v-slot:noResults>
                {{ $t('profile.not_found_location') }}
            </template>
        </C1Typeahead>

        <div
            v-if="locationsSelected.length"
            class="afu-mt-2"
        >
            <C1Tag
                v-for="location in locationsSelected"
                :key="`${location.value}_${location.id}`"
                :removable="!$_seekerFieldByCode('desired_locations').read_only"
                @remove="detachLocation(location)"
            >
                {{ location.label }}
            </C1Tag>
        </div>

        <div
            v-if="!locationsSelected.length"
            class="af-no-result-found afu-text-lg afu-font-semibold afu-mt-5 afu-text-neutral-dark"
        >
            <slot name="noResultText">
                {{ noResultText }}
            </slot>
        </div>
    </C1FormGroup>
</template>

<script>
    import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';
    import C1Tag from '@/components/C1Components/C1Tag';

    export default {
        name: 'SeekerLocations',

        components: { C1Typeahead, C1FormGroup, C1Tag },

        props: {
            inputPlaceholder: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                default: '',
            },

            hideLabel: {
                type: Boolean,
                default: false,
            },

            isRequired: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: `location_${this._uid}`,
                locationsSelected: [],
                locationsSearched: [],
            };
        },

        computed: {
            ...mapState({
                $_meWorkLocations: state => state.me.workLocations,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            noResultText() {
                return `No ${this.label.toLowerCase()} added.`;
            },

            validationRules() {
                return this.isRequired ? 'required|array' : '';
            },

            getPlaceholder() {
                return this.inputPlaceholder !== ''
                    ? this.inputPlaceholder
                    : this.$t('label.search_by_location');
            },

            getLabel() {
                return this.inputPlaceholder !== ''
                    ? this.inputPlaceholder
                    : this.$t('label.desired_work_locations');
            },
        },

        watch: {
            $_meWorkLocations: {
                handler() {
                    this.cloneLocations();
                },
                deep: true,
            },
        },

        created() {
            this.cloneLocations();
        },

        methods: {
            ...mapMutations('me', {
                $_STORE_WORK_LOCATION: 'STORE_WORK_LOCATION',
                $_PUT_WORK_LOCATION: 'PUT_WORK_LOCATION',
                $_DESTROY_WORK_LOCATION: 'DESTROY_WORK_LOCATION',
            }),

            ...mapActions('location', {
                $_getLocations: 'getLocations',
            }),

            ...mapActions('me', {
                $_attachWorkLocation: 'attachWorkLocation',
                $_detachWorkLocation: 'detachWorkLocation',
            }),

            cloneLocations() {
                this.locationsSelected = JSON.parse(
                    JSON.stringify(this.$_meWorkLocations),
                );
            },

            searchLocations(query) {
                this.$_getLocations({ query }).then(response => {
                    this.locationsSearched = response.data;
                });
            },

            attachLocation(location) {
                this.$_attachWorkLocation(location.id).then(() => {
                    this.$_PUT_WORK_LOCATION(location);
                });
            },

            detachLocation(location) {
                this.$set(location, 'is_loading', true);
                this.$_detachWorkLocation(location.id)
                    .then(() => {
                        this.$_DESTROY_WORK_LOCATION(location.id);
                    })
                    .catch(() => {
                        this.$set(location, 'is_loading', false);
                    });
            },
        },
    };
</script>
